import type React from "react";
import FilterIcon from "../SVGComponents/FilterIcon";
import styles from "./Filter.module.scss";
import Popover from "@mui/material/Popover";
import { useEffect, useMemo } from "react";
import { getInitialValues, pick, today } from "@/lib/utils";
import formRenderer from "@/lib/formRenderer";
import { useForm } from "react-hook-form";
import { Button, Divider } from "@mui/material";
import toast from "@/lib/toast";
import dayjs from "dayjs";

interface showFilter {
  status?: boolean;
  date?: boolean;
}

interface list {
  statusList: any[],
}

interface FilterProps {
  anchorEl: HTMLElement | null;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  onApply: (filters: { status?: string, fromDate?: string, toDate?: string }) => void;
  appliedFilter: { status?: string, fromDate?: string, toDate?: string };
  showFilter: showFilter,
  list?: list,
}

const Filter: React.FC<FilterProps> = ({
  anchorEl,
  onClick,
  onClose,
  onApply,
  appliedFilter,
  showFilter,
  list,
}) => {
  const open = Boolean(anchorEl);

  const { register, control, formState, watch, reset, getValues } = useForm({
    defaultValues: getInitialValues({}),
    mode: "onTouched",
  });

  const watchedFromDate = watch("fromDate");

  const formConfig = useMemo(
    () => ({
      status: {
        defaultValue: null,
        controlType: "select",
        placeholder: "Status",
        label: "By Status",
        options: list?.statusList || [],
        isControlled: true,
        isMulti: false,
      },
      fromDate: {
        defaultValue: null,
        controlType: "date",
        placeholder: "Date",
        label: "From Date",
        props: {
          max: today,
        },
      },
      toDate: {
        defaultValue: null,
        controlType: "date",
        placeholder: "Date",
        label: "To Date",
        props: {
          min: watchedFromDate || undefined,
          max: today,
        },
      },
    }),
    [watchedFromDate]
  );

  useEffect(() => {
    if (!open) return;

    reset({
      ...appliedFilter,
      fromDate: appliedFilter?.fromDate ? dayjs(appliedFilter.fromDate) : null,
      toDate: appliedFilter?.toDate ? dayjs(appliedFilter.toDate) : null,
    });
  }, [open, appliedFilter, reset]);

  const handleApply = () => {
    const values = getValues();

    if (
      values.fromDate &&
      values.toDate &&
      new Date(values.toDate) < new Date(values.fromDate)
    ) {
      toast.error("To Date cannot be before From Date");
      return;
    }

    onApply({
      status: values.status || "",
      fromDate: values.fromDate ? dayjs(values.fromDate).format("YYYY-MM-DD") : "",
      toDate: values.toDate ? dayjs(values.toDate).format("YYYY-MM-DD") : "",
    });

    onClose();
  };

  const handleClear = () => {
    reset({
      status: "",
      fromDate: null,
      toDate: null,
    });
    onApply({});
    onClose();
  };

  return (
    <>
      <span className={styles.filter} onClick={onClick}>
        <FilterIcon />
      </span>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <form className="p-2">
          {showFilter?.date && (
            <div className="grid grid-cols-2 gap-4 p-4">
              {formRenderer(pick(formConfig, ["fromDate", "toDate"]), formState, register, control)}
            </div>
          )}

          {showFilter?.status && (
            <div className="grid grid-cols-1 gap-1.5 p-4">
              {formRenderer(pick(formConfig, ["status"]), formState, register, control)}
            </div>
          )}

          <Divider />

          <div className="flex justify-end gap-4 p-3">
            <Button
              variant="contained"
              onClick={handleClear}
              style={{ backgroundColor: "white", color: "black" }}
            >
              Clear
            </Button>

            <Button variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </form>
      </Popover>
    </>
  );
};

export default Filter;
